// components/recent-data-tabs.tsx
import { BillingAdjustmentType, ConsumerBillType } from "@/types"
import { format } from "date-fns"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Icons } from "@/components/icons"

import { Badge } from "../ui/badge"

interface RecentDataTabsProps {
  activeTab: string
  setActiveTab: (tab: string) => void
  showAllPayments: boolean
  setShowAllPayments: (show: boolean) => void
  showAllConsumers: boolean
  setShowAllConsumers: (show: boolean) => void
  showAllConsumerBilling: boolean
  setShowAllConsumerBilling: (show: boolean) => void
  showAllBillAdjustment: boolean
  setShowAllBillAdjustment: (show: boolean) => void
  recentPayments: any[] | undefined
  isRecentPaymentsLoading: boolean
  consumerDetails: any[] | undefined
  isConsumerDetailsLoading: boolean
  recentConsumerBill: ConsumerBillType[] | undefined
  isRecentConsumerBillLoading: boolean
  recentBillAdjustment: BillingAdjustmentType[] | undefined
  isRecentBillAdjustmentLoading: boolean
  formatDate: (dateString: string) => string
}

export function RecentDataTabs({
  activeTab,
  setActiveTab,
  showAllPayments,
  setShowAllPayments,
  showAllConsumers,
  setShowAllConsumers,
  showAllConsumerBilling,
  setShowAllConsumerBilling,
  showAllBillAdjustment,
  setShowAllBillAdjustment,
  recentPayments,
  isRecentPaymentsLoading,
  consumerDetails,
  isConsumerDetailsLoading,
  recentConsumerBill,
  recentBillAdjustment,
  isRecentBillAdjustmentLoading,
  formatDate,
}: RecentDataTabsProps) {
  const renderBillingTable = () => {
    if (isConsumerDetailsLoading) {
      return (
        <Table>
          <TableBody>
            {[...Array(5)].map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-16" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-32" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-64" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-32" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-64" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )
    }

    if (!recentConsumerBill || recentConsumerBill.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={5} className="text-center">
            No billing data available
          </TableCell>
        </TableRow>
      )
    }
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Billing ID</TableHead>
            <TableHead>Consumer</TableHead>
            <TableHead>Zone</TableHead>
            <TableHead>Book</TableHead>
            <TableHead>Meter No.</TableHead>
            <TableHead>Acc No.</TableHead>
            <TableHead>Units</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Due Date</TableHead>
            <TableHead>Due Amount</TableHead>
            <TableHead>Total Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {recentConsumerBill
            .slice(0, showAllConsumerBilling ? 10 : 5)
            .map((billing) => (
              <TableRow key={billing.billing_id}>
                <TableCell>{billing.billing_id}</TableCell>
                <TableCell>{billing.consumer}</TableCell>
                <TableCell>{billing.zone}</TableCell>
                <TableCell>{billing.book}</TableCell>
                <TableCell>{billing.meter_num}</TableCell>
                <TableCell>{billing.acc_id}</TableCell>
                <TableCell>{billing.units}</TableCell>
                <TableCell>
                  {" "}
                  {billing.date
                    ? format(new Date(billing.date), "MMM dd, yyyy")
                    : ""}
                </TableCell>
                <TableCell>
                  {" "}
                  {billing.due_date
                    ? format(new Date(billing.due_date), "MMM dd, yyyy")
                    : ""}
                </TableCell>
                <TableCell>{billing.due_amount}</TableCell>
                <TableCell>{billing.total_amount}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    )
  }
  const renderBillAdjustmentTable = () => {
    if (isConsumerDetailsLoading) {
      return (
        <Table>
          <TableBody>
            {[...Array(5)].map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-16" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-32" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-64" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-32" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-64" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )
    }

    if (!recentBillAdjustment) {
      return (
        <TableRow>
          <TableCell colSpan={5} className="text-center">
            No bill adjustment data available
          </TableCell>
        </TableRow>
      )
    }
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Bill Adjust ID</TableHead>
            <TableHead>Bill ID</TableHead>
            <TableHead>Consumer</TableHead>
            <TableHead>Prev Consumption</TableHead>
            <TableHead>Prev Due Amount</TableHead>
            <TableHead>New Consumption</TableHead>
            <TableHead>New Due Amount</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>Notes</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Adjustment Date</TableHead>
            <TableHead>Requested By</TableHead>
            <TableHead>Date Approve/Denied</TableHead>
            <TableHead>Approved/Denied By</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.isArray(recentBillAdjustment)
            ? recentBillAdjustment
                .slice(0, showAllBillAdjustment ? 10 : 5)
                .map((billAdjustment: BillingAdjustmentType) => (
                  <TableRow key={billAdjustment.adjustment_id}>
                    <TableCell>{billAdjustment.adjustment_id}</TableCell>
                    <TableCell>{billAdjustment.billing_id}</TableCell>
                    <TableCell>{billAdjustment.consumer_name}</TableCell>
                    <TableCell>{billAdjustment.previous_consumption}</TableCell>
                    <TableCell>{billAdjustment.previous_due_amount}</TableCell>
                    <TableCell>{billAdjustment.new_consumption}</TableCell>
                    <TableCell>{billAdjustment.new_due_amount}</TableCell>
                    <TableCell>{billAdjustment.adjustment_reason}</TableCell>
                    <TableCell>{billAdjustment.adjustment_notes}</TableCell>
                    <TableCell>
                      {(() => {
                        const status = billAdjustment.adjustment_status
                        const statusClasses: any = {
                          PENDING: {
                            base: "bg-[#e3f2fd] text-[#2196F3]",
                            hover: "hover:bg-[#bbdefb]",
                          },
                          APPROVED: {
                            base: "bg-[#e6f7e6] text-[#00C853]",
                            hover: "hover:bg-[#c8e6c9]",
                          },
                          DENIED: {
                            base: "bg-[#ffebee] text-[#F44336]",
                            hover: "hover:bg-[#ffcdd2]",
                          },
                        }

                        const currentStatus = statusClasses[
                          status as keyof typeof statusClasses
                        ] || {
                          base: "bg-gray-100 text-gray-600",
                          hover: "hover:bg-gray-200",
                        }

                        return (
                          <Badge
                            className={`px-2 py-1 transition-colors duration-200 print:text-xl ${
                              currentStatus.base
                            } ${currentStatus.hover}`}
                          >
                            {status}
                          </Badge>
                        )
                      })()}
                    </TableCell>
                    <TableCell>
                      {billAdjustment.adjustment_date
                        ? format(
                            new Date(billAdjustment.adjustment_date),
                            "MMM dd, yyyy",
                          )
                        : ""}
                    </TableCell>
                    <TableCell>{billAdjustment.requested_by}</TableCell>
                    <TableCell>
                      {billAdjustment.date_approved_denied
                        ? format(
                            new Date(billAdjustment.date_approved_denied),
                            "MMM dd, yyyy",
                          )
                        : ""}
                    </TableCell>
                    <TableCell>{billAdjustment.approved_denied_by}</TableCell>
                  </TableRow>
                ))
            : []}
        </TableBody>
      </Table>
    )
  }
  const renderPaymentsTable = () => {
    if (isRecentPaymentsLoading) {
      return (
        <Table>
          <TableBody>
            {[...Array(5)].map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-32" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )
    }

    return recentPayments && recentPayments.length > 0 ? (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Consumer</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {recentPayments.slice(0, showAllPayments ? 10 : 5).map((payment) => (
            <TableRow key={payment.payment_id}>
              <TableCell>{payment.consumer_name}</TableCell>
              <TableCell>₱{payment.amount_paid.toFixed(2)}</TableCell>
              <TableCell>{formatDate(payment.payment_date)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    ) : (
      <TableRow>
        <TableCell colSpan={3} className="text-center">
          No recent payments available
        </TableCell>
      </TableRow>
    )
  }

  const renderConsumerTable = () => {
    if (isConsumerDetailsLoading) {
      return (
        <Table>
          <TableBody>
            {[...Array(5)].map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-16" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-32" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-64" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )
    }

    if (!consumerDetails || consumerDetails.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={5} className="text-center">
            No consumer data available
          </TableCell>
        </TableRow>
      )
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Zone</TableHead>
            <TableHead>Book</TableHead>
            <TableHead>Consumer</TableHead>
            <TableHead>Address</TableHead>
            <TableHead>Acc No.</TableHead>
            <TableHead>Connection Status</TableHead>
            <TableHead>Connection Type</TableHead>
            <TableHead>Meter No.</TableHead>
            <TableHead>Contact No.</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {consumerDetails
            .slice(0, showAllConsumers ? 10 : 5)
            .map((consumer) => (
              <TableRow key={consumer.consumer_id}>
                <TableCell>{consumer.consumer_id}</TableCell>
                <TableCell>{consumer.zone}</TableCell>
                <TableCell>{consumer.books}</TableCell>
                <TableCell>{consumer.consumer}</TableCell>
                <TableCell>
                  <div
                    className="max-h-[70px] w-[420px] max-w-[420px] overflow-auto text-right md:max-w-full md:py-0 md:text-left"
                    title={consumer.address}
                  >
                    {consumer.address}
                  </div>
                </TableCell>
                <TableCell>{consumer.acc_id}</TableCell>
                <TableCell>
                  {(() => {
                    const status = consumer.connection_status
                    interface StatusClasses {
                      base: string
                      hover: string
                    }

                    const statusClasses: Record<string, StatusClasses> = {
                      Pending: {
                        base: "bg-[#e3f2fd] text-[#2196F3]",
                        hover: "hover:bg-[#bbdefb]",
                      },
                      Inactive: {
                        base: "bg-[#e5e7eb] text-[#6B7280]",
                        hover: "hover:bg-[#d1d5db]",
                      },
                      Active: {
                        base: "bg-[#e6f7e6] text-[#00C853]",
                        hover: "hover:bg-[#c8e6c9]",
                      },
                      Delinquent: {
                        base: "bg-[#fff0cc] text-[#F8961E]",
                        hover: "hover:bg-[#ffe0b2]",
                      },
                      Disconnected: {
                        base: "bg-[#ffebee] text-[#F44336]",
                        hover: "hover:bg-[#ffcdd2]",
                      },
                    }

                    const currentStatus = statusClasses[status] || {
                      base: "bg-gray-100 text-gray-600",
                      hover: "hover:bg-gray-200",
                    }

                    return (
                      <Badge
                        className={`px-2 py-1 transition-colors duration-200 print:text-xl ${
                          currentStatus.base
                        } ${currentStatus.hover}`}
                      >
                        {status}
                      </Badge>
                    )
                  })()}
                </TableCell>
                <TableCell>{consumer.connection_type}</TableCell>
                <TableCell>{consumer.meter_num}</TableCell>
                <TableCell>{consumer.contact_num}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
        <div className="overflow-x-auto p-0">
          <TabsList className="m-0 flex h-12 w-max items-center overflow-hidden rounded-lg border-2 bg-white p-0">
            <TabsTrigger
              value="Recent Billings"
              className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
            >
              Recent Billings
            </TabsTrigger>
            <TabsTrigger
              value="Recently Added Consumers"
              className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
            >
              Recently Added Consumers
            </TabsTrigger>
            <TabsTrigger
              value="Recent Bill Adjustments"
              className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
            >
              Recent Bill Adjustments
            </TabsTrigger>
            <TabsTrigger
              value="Recent Payments"
              className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
            >
              Recent Payments
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="Recent Billings" className="min-h-0 flex-1">
          <Card className="h-full">
            <CardContent className="h-[calc(100%-57px)] pl-2 pr-10">
              <div className="flex items-center justify-end py-2">
                {recentConsumerBill && recentConsumerBill.length > 5 && (
                  <Button
                    className="mt-0"
                    onClick={() =>
                      setShowAllConsumerBilling(!showAllConsumerBilling)
                    }
                  >
                    {showAllConsumerBilling ? "See Less" : "See More"}
                  </Button>
                )}
              </div>
              <div className="overflow-x-auto">{renderBillingTable()}</div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent
          value="Recently Added Consumers"
          className="min-h-0 flex-1"
        >
          <Card className="h-full">
            <CardContent className="h-[calc(100%-57px)] pl-2 pr-10">
              <div className="flex items-center justify-end py-2">
                {consumerDetails && consumerDetails.length > 5 && (
                  <Button
                    className="mt-0"
                    onClick={() => setShowAllConsumers(!showAllConsumers)}
                  >
                    {showAllConsumers ? "See Less" : "See More"}
                  </Button>
                )}
              </div>
              <div className="overflow-x-auto">{renderConsumerTable()}</div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="Recent Bill Adjustments" className="min-h-0 flex-1">
          <Card className="h-full">
            <CardContent className="h-[calc(100%-57px)] pl-2 pr-10">
              <div className="flex items-center justify-end py-2">
                {recentBillAdjustment && recentBillAdjustment.length > 5 && (
                  <Button
                    className="mt-0"
                    onClick={() =>
                      setShowAllBillAdjustment(!showAllBillAdjustment)
                    }
                  >
                    {showAllBillAdjustment ? "See Less" : "See More"}
                  </Button>
                )}
              </div>
              <div className="overflow-x-auto">
                {renderBillAdjustmentTable()}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="Recent Payments" className="min-h-0 flex-1">
          <Card className="h-full">
            <CardContent className="h-[calc(100%-57px)] pl-2 pr-10">
              <div className="flex items-center justify-end py-2">
                {recentPayments && recentPayments.length > 5 && (
                  <Button
                    className="mt-0"
                    onClick={() => setShowAllPayments(!showAllPayments)}
                  >
                    {showAllPayments ? "See Less" : "See More"}
                  </Button>
                )}
              </div>
              <div className="overflow-x-auto">{renderPaymentsTable()}</div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
